import { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "../ui/button";
import { RotateCw, Trophy } from "lucide-react";

const SIZE = 4;
const emptyBoard = () => Array(SIZE).fill(0).map(() => Array(SIZE).fill(0));

function addRandom(board) {
    const empties = [];
    for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) if (board[r][c] === 0) empties.push([r, c]);
    if (empties.length === 0) return board;
    const [r, c] = empties[Math.floor(Math.random() * empties.length)];
    board[r][c] = Math.random() < 0.9 ? 2 : 4;
    return board;
}

function clone(b) { return b.map((r) => [...r]); }

function slideLeft(row) {
    const filtered = row.filter((v) => v !== 0);
    let gained = 0;
    for (let i = 0; i < filtered.length - 1; i++) {
        if (filtered[i] === filtered[i + 1]) {
            filtered[i] *= 2;
            gained += filtered[i];
            filtered.splice(i + 1, 1);
        }
    }
    while (filtered.length < SIZE) filtered.push(0);
    return { row: filtered, gained };
}

function move(board, dir) {
    let b = clone(board);
    let gained = 0;
    const transpose = (bb) => bb[0].map((_, i) => bb.map((r) => r[i]));
    const reverseRows = (bb) => bb.map((r) => [...r].reverse());

    if (dir === "left") {
        for (let r = 0; r < SIZE; r++) {
            const out = slideLeft(b[r]);
            b[r] = out.row; gained += out.gained;
        }
    } else if (dir === "right") {
        b = reverseRows(b);
        for (let r = 0; r < SIZE; r++) {
            const out = slideLeft(b[r]);
            b[r] = out.row; gained += out.gained;
        }
        b = reverseRows(b);
    } else if (dir === "up") {
        b = transpose(b);
        for (let r = 0; r < SIZE; r++) {
            const out = slideLeft(b[r]);
            b[r] = out.row; gained += out.gained;
        }
        b = transpose(b);
    } else if (dir === "down") {
        b = transpose(b);
        b = reverseRows(b);
        for (let r = 0; r < SIZE; r++) {
            const out = slideLeft(b[r]);
            b[r] = out.row; gained += out.gained;
        }
        b = reverseRows(b);
        b = transpose(b);
    }
    return { board: b, gained };
}

function boardsEqual(a, b) {
    for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) if (a[r][c] !== b[r][c]) return false;
    return true;
}

function canMove(board) {
    return ["left", "right", "up", "down"].some((d) => !boardsEqual(board, move(board, d).board));
}

export function Game2048({ onScoreSubmit }) {
    const [board, setBoard] = useState(() => addRandom(addRandom(emptyBoard())));
    const [score, setScore] = useState(0);
    const [best, setBest] = useState(() => parseInt(localStorage.getItem("crm_2048_best") || "0", 10));
    const [over, setOver] = useState(false);
    const startedAt = useRef(Date.now());
    const submitted = useRef(false);

    const tryMove = useCallback((dir) => {
        if (over) return;
        const { board: nb, gained } = move(board, dir);
        if (boardsEqual(board, nb)) return;
        const finalBoard = addRandom(clone(nb));
        const newScore = score + gained;
        setBoard(finalBoard);
        setScore(newScore);
        if (newScore > best) {
            setBest(newScore);
            localStorage.setItem("crm_2048_best", String(newScore));
        }
        if (!canMove(finalBoard)) {
            setOver(true);
            if (!submitted.current) {
                submitted.current = true;
                onScoreSubmit?.({ score: newScore, durationMs: Date.now() - startedAt.current });
            }
        }
    }, [board, score, best, over, onScoreSubmit]);

    useEffect(() => {
        const onKey = (e) => {
            const dir = { ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down" }[e.key];
            if (dir) { e.preventDefault(); tryMove(dir); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [tryMove]);

    const reset = () => {
        setBoard(addRandom(addRandom(emptyBoard())));
        setScore(0);
        setOver(false);
        submitted.current = false;
        startedAt.current = Date.now();
    };

    return (
        <div className="flex flex-col items-center">
            <div className="flex items-center justify-between w-full max-w-md mb-3">
                <div className="flex gap-2">
                    <ScoreBox label="Score" value={score} />
                    <ScoreBox label="Best" value={best} accent />
                </div>
                <Button size="sm" variant="outline" onClick={reset}><RotateCw className="h-3.5 w-3.5 mr-1" />New game</Button>
            </div>

            <div className="bg-gray-200 p-2 rounded-xl" style={{ width: "min(92vw, 420px)" }}>
                <div className="grid grid-cols-4 gap-2">
                    {board.flat().map((v, i) => (
                        <div
                            key={i}
                            className={`tile tile-${v} aspect-square text-3xl`}
                        >
                            {v || ""}
                        </div>
                    ))}
                </div>
            </div>

            {over && (
                <div className="mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-center max-w-md">
                    <Trophy className="h-5 w-5 text-amber-600 inline mb-1" />
                    <div className="text-sm font-semibold text-amber-800">Game Over — final score: {score}</div>
                    <Button size="sm" onClick={reset} className="mt-2 bg-amber-600 hover:bg-amber-700 text-white">Play again</Button>
                </div>
            )}

            <p className="text-[11px] text-gray-400 mt-3">Use arrow keys ← → ↑ ↓ to merge tiles</p>
        </div>
    );
}

function ScoreBox({ label, value, accent }) {
    return (
        <div className={`px-3 py-1.5 rounded-md text-center ${accent ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-700"}`}>
            <div className="text-[10px] uppercase font-semibold tracking-wide opacity-70">{label}</div>
            <div className="text-base font-bold tabular-nums">{value}</div>
        </div>
    );
}
